import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe, Shield, Sparkles, ArrowRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage({ onLogin }) {
  const { t, i18n } = useTranslation();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    teoudat_zehout: '',
    code: '',
    full_name: '',
    email: '',
    preferred_language: 'en'
  });

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'he', name: 'עברית' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'ru', name: 'Русский' },
    { code: 'ar', name: 'العربية' },
    { code: 'it', name: 'Italiano' },
    { code: 'pt', name: 'Português' },
    { code: 'zh', name: '中文' },
    { code: 'uk', name: 'Українська' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? `${API}/auth/register` : `${API}/auth/login`;
      const payload = isRegister
        ? formData
        : { teoudat_zehout: formData.teoudat_zehout, code: formData.code };

      const response = await axios.post(endpoint, payload);
      toast.success(isRegister ? t('register_success') : t('login_success'));
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background with mesh gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-slate-50" />
      <div className="absolute inset-0 gradient-mesh opacity-60" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse-slow" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Language Selector */}
          <div className="flex justify-end mb-4">
            <Select value={i18n.language} onValueChange={(value) => {
              i18n.changeLanguage(value);
              // Save the selected language so it persists after login
              localStorage.setItem('selected_language', value);
            }}>
              <SelectTrigger className="w-40 bg-white/80 backdrop-blur-sm border-white/50 shadow-lg hover:bg-white transition-all rounded-xl" data-testid="language-selector">
                <Globe className="h-4 w-4 mr-2 text-blue-600" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code} data-testid={`lang-${lang.code}`}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Auth Card */}
          <div className="glass-card rounded-3xl p-8 md:p-10 animate-fadeIn">
            {/* Logo & Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg shadow-blue-500/30 mb-4 animate-float">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent mb-2" data-testid="auth-title">
                {t('app_name')}
              </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="teoudat_zehout" className="text-sm font-semibold text-slate-700">
                  {t('teoudat_zehout')}
                </Label>
                <div className="relative">
                  <Input
                    id="teoudat_zehout"
                    type="text"
                    value={formData.teoudat_zehout}
                    onChange={(e) => setFormData({ ...formData, teoudat_zehout: e.target.value })}
                    className="h-12 rounded-xl text-base pl-4 pr-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
                    placeholder="123456789"
                    required
                    data-testid="input-teoudat-zehout"
                  />
                  <Shield className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm font-semibold text-slate-700">
                  {t('code')}
                </Label>
                <Input
                  id="code"
                  type="password"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="h-12 rounded-xl text-base border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
                  placeholder="••••••••"
                  required
                  data-testid="input-code"
                />
              </div>

              {isRegister && (
                <div className="space-y-5 animate-fadeIn">
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-sm font-semibold text-slate-700">
                      {t('full_name')}
                    </Label>
                    <Input
                      id="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="h-12 rounded-xl text-base border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
                      placeholder="John Doe"
                      required
                      data-testid="input-full-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-700">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-12 rounded-xl text-base border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
                      placeholder="david@example.com"
                      data-testid="input-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferred_language" className="text-sm font-semibold text-slate-700">
                      {t('language')}
                    </Label>
                    <Select
                      value={formData.preferred_language}
                      onValueChange={(value) => setFormData({ ...formData, preferred_language: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-preferred-language">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        {languages.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 group"
                data-testid="submit-button"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    {isRegister ? t('create_account') : t('login')}
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isRegister ? t('already_have_account') : t('dont_have_account')}
              </button>
            </div>
          </div>

          <p className="text-center text-slate-500 mt-6 text-sm">
            {isRegister ? t('register_prompt') : t('login_prompt')}
          </p>
          
          {/* Trust badges */}
          <div className="flex items-center justify-center gap-4 mt-6 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <Shield className="h-3 w-3" /> Secure
            </span>
            <span>•</span>
            <span>256-bit encryption</span>
            <span>•</span>
            <span>Gov. Certified</span>
          </div>
        </div>
      </div>
    </div>
  );
}